/**
 * Hook for fetching user feature settings from the API
 * Uses React Query for caching and deduplication across components
 */

import { useOptionalAuth } from '@/contexts/AuthContext';
import type { NoteDetail, NoteFormat } from '@/types/noteStyle';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

// Browser-local key for the streaming-dictation beta preference. Kept in
// localStorage (not the DB-backed user_settings table) while the feature is
// in beta dogfooding — once it graduates, migrate to a real features.*
// column the same way asr_model_preference is persisted.
const STREAMING_DICTATION_STORAGE_KEY = 'careflux:streaming_dictation';

export interface Features {
  enable_rule_based_normalization?: boolean;
  summary_font_size?: number;
  asr_model_preference?: string | null;
  // Scribe User Memory flag (Minni) — absence means ENABLED; only the
  // clinician's own toggle (explicit false) turns it off.
  user_memory_enabled?: boolean;
  transcription_language?: string | null;
  summary_language?: string | null;
  // Default note style for generated notes; unset adds no style instruction.
  default_note_detail?: NoteDetail | null;
  default_note_format?: NoteFormat | null;
  // First-run session tips: "pending" after a first onboarding, "done" once finished or skipped.
  session_tutorial?: 'pending' | 'done';
  [key: string]: unknown;
}

interface SettingsResponse {
  features: Features;
}

interface FeatureSettings {
  enableRuleBasedNormalization: boolean;
  summaryFontSize: number;
  setSummaryFontSize: (size: number) => void;
  asrModelPreference: string | null;
  /**
   * Beta-only opt-in for the streaming dictation flow (BFF bridges to ASR
   * /stream/ws). Stored in localStorage — browser-local; cross-device sync
   * happens when this graduates from beta and we add a DB-backed column.
   */
  streamingDictation: boolean;
  setStreamingDictation: (enabled: boolean) => void;
  userMemoryEnabled: boolean;
  setUserMemoryEnabled: (enabled: boolean) => void;
  transcriptionLanguage: string | null;
  summaryLanguage: string | null;
  setTranscriptionLanguage: (language: string) => void;
  setSummaryLanguage: (language: string) => void;
  defaultNoteDetail: NoteDetail;
  /** null until the user picks one: the template then decides the layout. */
  defaultNoteFormat: NoteFormat | null;
  /** Persist a partial features patch (only these keys are written). */
  setFeatures: (updates: Record<string, unknown>, options?: SetFeaturesOptions) => void;
  /** Raw features map, for pages that edit several keys at once. */
  features: Features;
  isSaving: boolean;
  isLoading: boolean;
  /** True once the settings fetch has succeeded (false while loading or after an error). */
  isReady: boolean;
}

export interface SetFeaturesOptions {
  onSuccess?: () => void;
  onError?: () => void;
}

interface UseSettingsOptions {
  /** Disable the network fetch/persistence (e.g. in the tokenless embed). */
  enabled?: boolean;
}

const DEFAULT_SUMMARY_FONT_SIZE = 14;
// Stable identity so consumers can depend on `features` in effects.
const NO_FEATURES: Features = {};

/** Settings are per person/account: key by the active auth account so a switch never reuses another's cache. */
export const settingsQueryKey = (accountId: string | null) => ['settings', accountId] as const;

export const fetchSettings = async (): Promise<SettingsResponse> => {
  const res = await fetch('/api/settings', {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error('Failed to fetch settings');
  return res.json();
};

/** POST only the changed keys; the backend merges them into the stored features. */
export const patchSettings = async (updates: Record<string, unknown>, signal?: AbortSignal): Promise<void> => {
  const res = await fetch('/api/settings/update', {
    method: 'POST',
    signal,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ features: updates }),
  });
  if (!res.ok) throw new Error('Failed to update settings');
};

export const useSettings = ({ enabled = true }: UseSettingsOptions = {}): FeatureSettings => {
  const queryClient = useQueryClient();
  const auth = useOptionalAuth();
  const queryKey = settingsQueryKey(auth?.user?.authAccountId ?? auth?.user?.id ?? null);

  const { data, isLoading, isSuccess } = useQuery({
    queryKey,
    queryFn: fetchSettings,
    staleTime: 60_000,
    enabled,
  });

  const features = data?.features ?? NO_FEATURES;

  // Sends a partial patch (never the whole cached map): a full-object write could
  // resend stale or org-hidden keys and overwrite what the server holds.
  const patchFeatureMutation = useMutation({
    mutationFn: (updates: Record<string, unknown>) => patchSettings(updates),
    onMutate: async (updates) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<SettingsResponse>(queryKey);
      queryClient.setQueryData<SettingsResponse>(queryKey, (old) => ({
        ...old,
        features: { ...(old?.features || {}), ...updates },
      }));
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const setFeatures = (updates: Record<string, unknown>, options?: SetFeaturesOptions) =>
    patchFeatureMutation.mutate(updates, options);

  const setSummaryFontSize = (size: number) => {
    setFeatures({ summary_font_size: size });
  };

  const setTranscriptionLanguage = (language: string) => {
    setFeatures({ transcription_language: language });
  };

  const setSummaryLanguage = (language: string) => {
    setFeatures({ summary_language: language });
  };

  const setUserMemoryEnabled = (enabled: boolean) => {
    setFeatures({ user_memory_enabled: enabled });
  };

  // Streaming dictation: localStorage-backed, no server roundtrip. Read on
  // mount + write through; useState gives us a stable identity so consumers
  // can rely on it for dictation-start handshakes without re-reading
  // localStorage on every render.
  const [streamingDictation, setStreamingDictationState] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(STREAMING_DICTATION_STORAGE_KEY) === 'true';
  });

  // Sync across tabs — `storage` event fires when localStorage is changed in
  // another window. Without this, a clinician toggling on tab A wouldn't see
  // the change on tab B until refresh.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STREAMING_DICTATION_STORAGE_KEY) return;
      setStreamingDictationState(e.newValue === 'true');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setStreamingDictation = (enabled: boolean) => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STREAMING_DICTATION_STORAGE_KEY, enabled ? 'true' : 'false');
    }
    setStreamingDictationState(enabled);
  };

  return {
    enableRuleBasedNormalization: features.enable_rule_based_normalization || false,
    summaryFontSize: features.summary_font_size || DEFAULT_SUMMARY_FONT_SIZE,
    setSummaryFontSize,
    asrModelPreference: features.asr_model_preference || null,
    streamingDictation,
    setStreamingDictation,
    userMemoryEnabled: features.user_memory_enabled ?? true,
    setUserMemoryEnabled,
    transcriptionLanguage: features.transcription_language ?? null,
    summaryLanguage: features.summary_language ?? null,
    setTranscriptionLanguage,
    setSummaryLanguage,
    defaultNoteDetail: features.default_note_detail ?? 'balanced',
    defaultNoteFormat: features.default_note_format ?? null,
    setFeatures,
    features,
    isSaving: patchFeatureMutation.isPending,
    isLoading,
    isReady: isSuccess,
  };
};
